import { useState, useRef, useCallback, useEffect } from 'react';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useGlobalHighlight } from '@/hooks/useGlobalHighlight';

export function useObjectiveSelector() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [localNeedsAttention, setLocalNeedsAttention] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { needsHighlight, setNeedsHighlight, setHighlightEnabled } = useGlobalHighlight();

  useClickOutside(dropdownRef, () => {
    if (isDropdownOpen) {
      setIsDropdownOpen(false);
    }
  });

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(prev => !prev);
    if (localNeedsAttention) {
      setLocalNeedsAttention(false);
    }
    if (needsHighlight) {
      setNeedsHighlight(false);
    }
  }, [localNeedsAttention, needsHighlight, setNeedsHighlight]);

  const highlightDropdown = useCallback(() => {
    // Only set attention if highlighting is enabled
    if (useHighlightStore.getState().highlightEnabled) {
      setLocalNeedsAttention(true);
    }
  }, []);

  const clearHighlight = useCallback(() => {
    setLocalNeedsAttention(false);
    setNeedsHighlight(false);
  }, [setNeedsHighlight]);

  useEffect(() => {
    if (isDropdownOpen) {
      clearHighlight();
    }
  }, [isDropdownOpen, clearHighlight]);

  useEffect(() => {
    if (needsHighlight) {
      // Only set attention if highlighting is enabled
      if (useHighlightStore.getState().highlightEnabled) {
        setLocalNeedsAttention(true);
      }
    }
  }, [needsHighlight]);

  return {
    isDropdownOpen,
    needsAttention: localNeedsAttention || needsHighlight,
    dropdownRef,
    toggleDropdown,
    highlightDropdown,
    clearHighlight
  };
}